/**
 * @file     ListingViewTracker.tsx
 * @purpose  Mount-once + unmount-dwell tracker for property detail pages.
 *           Fires listing.view on mount and listing.dwell on unmount with
 *           the elapsed ms. Pure-effect, renders nothing.
 */
'use client';

import { useEffect } from 'react';
import { reltaTrack } from '@/hooks/useReltaTrack';

interface Props {
  listingKey: string;
  listingSlug?: string;
  listPrice?: number | null;
  area?: string | null;
  buildingKey?: string | null;
  source?: string;
}

export default function ListingViewTracker({
  listingKey,
  listingSlug,
  listPrice,
  area,
  buildingKey,
  source,
}: Props) {
  useEffect(() => {
    reltaTrack('listing.view', {
      listing_key: listingKey,
      ...(listingSlug ? { listing_slug: listingSlug } : {}),
      ...(typeof listPrice === 'number' ? { list_price: listPrice } : {}),
      ...(area ? { area } : {}),
      ...(buildingKey ? { building_key: buildingKey } : {}),
      ...(source ? { source } : {}),
    });
    const startedAt = performance.now();
    return () => {
      const dwellMs = Math.round(performance.now() - startedAt);
      if (dwellMs >= 1000) {
        reltaTrack('listing.dwell', { listing_key: listingKey, dwell_ms: dwellMs });
      }
    };
  }, [listingKey, listingSlug, listPrice, area, buildingKey, source]);

  return null;
}
